import { createPortal } from "preact/compat";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "preact/hooks";

import styles from "./ContextMenu.module.css";

export interface ContextMenuItem {
  readonly label: string;
  readonly disabled?: boolean;
  readonly separatorBefore?: boolean;
  readonly onSelect: () => void;
}

interface ContextMenuProps {
  readonly x: number;
  readonly y: number;
  readonly items: readonly ContextMenuItem[];
  readonly onClose: () => void;
}

interface MenuPosition {
  readonly x: number;
  readonly y: number;
}

const VIEWPORT_MARGIN = 6;

export function ContextMenu({
  x,
  y,
  items,
  onClose,
}: ContextMenuProps) {
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<MenuPosition>({ x, y });

  useLayoutEffect(() => {
    const element = menu.current;
    if (element === null) {
      return;
    }

    const maximumX = Math.max(
      VIEWPORT_MARGIN,
      window.innerWidth - element.offsetWidth - VIEWPORT_MARGIN,
    );
    const maximumY = Math.max(
      VIEWPORT_MARGIN,
      window.innerHeight - element.offsetHeight - VIEWPORT_MARGIN,
    );
    setPosition({
      x: Math.min(Math.max(VIEWPORT_MARGIN, x), maximumX),
      y: Math.min(Math.max(VIEWPORT_MARGIN, y), maximumY),
    });

    const firstEnabled = element.querySelector<HTMLButtonElement>(
      "button:not(:disabled)",
    );
    firstEnabled?.focus();
  }, [x, y, items]);

  useEffect(() => {
    const closeOnPointerDown = (event: PointerEvent): void => {
      if (!menu.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    const closeOnContextMenu = (event: MouseEvent): void => {
      if (!menu.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    const closeOnKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener("pointerdown", closeOnPointerDown);
    window.addEventListener("contextmenu", closeOnContextMenu);
    window.addEventListener("keydown", closeOnKeyDown);
    window.addEventListener("resize", onClose);
    window.addEventListener("blur", onClose);
    return () => {
      window.removeEventListener("pointerdown", closeOnPointerDown);
      window.removeEventListener("contextmenu", closeOnContextMenu);
      window.removeEventListener("keydown", closeOnKeyDown);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("blur", onClose);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={menu}
      class={styles.menu}
      role="menu"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {items.map((item, index) => (
        <div key={`${item.label}:${index}`}>
          {item.separatorBefore ? (
            <div class={styles.separator} role="separator" />
          ) : null}
          <button
            type="button"
            class={styles.item}
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              onClose();
              item.onSelect();
            }}
          >
            {item.label}
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
